import { useState, useEffect, useCallback } from "react";
import { BarChart3, Package, FolderTree, Image, FileText, TrendingUp, Clock } from "lucide-react";
import adminApi from "@/lib/adminApi";

function StatCard({ label, value, icon: Icon, color = "text-[#CBA052]" }) {
  return (
    <div className="border border-white/[0.06] bg-white/[0.02] rounded p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={color} />
        <span className="text-[10px] text-white/40 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminApi.get("/api/admin/dashboard/stats");
      setStats(res.data);
    } catch (err) { console.error("[AdminDashboard] fetch stats failed:", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="text-xs text-white/30 text-center py-8">Loading dashboard...</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats-grid">
        <StatCard label="Products" value={stats?.products || 0} icon={Package} />
        <StatCard label="Categories" value={stats?.categories || 0} icon={FolderTree} />
        <StatCard label="Media Files" value={stats?.media || 0} icon={Image} />
        <StatCard label="Quotes" value={stats?.quotes || 0} icon={FileText} color="text-green-400" />
      </div>

      {/* Recent Quotes */}
      <div className="border border-white/[0.08] bg-[#060606]" data-testid="dashboard-recent-quotes">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <Clock size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Recent Quotes</h3>
        </div>
        <div className="p-5">
          {!stats?.recent_quotes?.length ? (
            <p className="text-xs text-white/30 text-center py-4">No quotes submitted yet.</p>
          ) : (
            <div className="space-y-2">
              {stats.recent_quotes.map((q, i) => (
                <div key={`quote-${i}`} className="flex items-center gap-3 border border-white/[0.04] bg-white/[0.01] rounded p-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-white truncate">{q.name || "Anonymous"}</span>
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-[#CBA052]/15 text-[#CBA052] border-[#CBA052]/20">{q.project_type || "Quote"}</span>
                    </div>
                    <p className="text-[10px] text-white/30 truncate">{q.email || "—"} &middot; {q.phone || "—"}</p>
                  </div>
                  <span className="text-[10px] text-white/20 shrink-0">
                    {q.submitted_at ? new Date(q.submitted_at).toLocaleDateString() : "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
